import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, Link } from '@tanstack/react-router';
import { Badge, type BadgeVariant } from '@heyroot/ui/primitives/Badge';
import { Button } from '@heyroot/ui/primitives/Button';
import { EmptyState } from '@heyroot/ui/primitives/EmptyState';
import { Input } from '@heyroot/ui/primitives/Input';
import { Skeleton } from '@heyroot/ui/primitives/Skeleton';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableHeaderCell,
} from '@heyroot/ui/primitives/Table';
import {
  fetchPerson,
  sendPasswordReset,
  setUserAccountStatus,
  queryKeys,
  AdminApiError,
} from '../lib/api';
import { useAdminAuth } from '../lib/auth-context';
import { PERMS } from '../../rbac/permissions';

const ERROR_COPY: Record<string, string> = {
  // I2 fix (whole-branch review): the list shows membership-less shadow
  // identities on purpose (users-repo.ts's LEFT JOIN), but both write routes
  // 404 USER_NOT_FOUND when the target has no ACTIVE tenant membership --
  // "no longer exists" was actively false for a person the list just showed.
  // The two causes (truly gone vs. no active membership) share one wire code,
  // so this copy stays honest for either without guessing which applies.
  USER_NOT_FOUND:
    "This account action isn't available -- the person has no active tenant membership right now.",
  INVALID_BODY: 'That request was not valid. Reload the page and try again.',
};

function statusVariant(status: string): BadgeVariant {
  switch (status) {
    case 'active':
      return 'success';
    case 'suspended':
      return 'warning';
    case 'removed':
      return 'danger';
    default:
      return 'neutral';
  }
}

// Fix round 2 finding 1: identity.users.status has two reachable values
// (locked, deleted) that tenant_users.status doesn't -- a separate map keeps
// the account-status badge honest instead of silently falling through the
// membership map's `default: 'neutral'`.
function accountStatusVariant(status: string): BadgeVariant {
  switch (status) {
    case 'active':
      return 'success';
    case 'suspended':
      return 'warning';
    case 'locked':
      return 'warning';
    case 'deleted':
      return 'danger';
    default:
      return 'neutral';
  }
}

export function UserDetailPage() {
  const { userId } = useParams({ strict: false }) as { userId: string };
  const { hasPerm } = useAdminAuth();
  const qc = useQueryClient();
  const canManage = hasPerm(PERMS.USERS_MANAGE);

  const [confirmSuspend, setConfirmSuspend] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [resetSent, setResetSent] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.person(userId),
    queryFn: () => fetchPerson(userId),
  });

  const resetMutation = useMutation({
    mutationFn: () => sendPasswordReset(userId),
    // onSuccess/onError stay for real react-query's benefit (e.g. a future
    // query invalidation); the click handler below also handles both inline
    // since a mock useMutation never invokes these.
    onSuccess: () => {
      setError(null);
      setResetSent(true);
    },
    onError: (err: unknown) => {
      setError(
        err instanceof AdminApiError
          ? (ERROR_COPY[err.code] ?? err.message)
          : 'Could not send the password reset.',
      );
    },
  });

  const statusMutation = useMutation({
    // C2 fix (whole-branch review): omit `reason` entirely when empty, rather
    // than sending `reason: ''` -- the server schema is
    // `z.string().trim().min(1).max(500).optional()`, and an empty string is
    // PRESENT so .optional() never applies and .min(1) 400s. Reactivate (which
    // never collects a reason) was unreachable without this.
    mutationFn: (status: 'active' | 'suspended') => {
      const r = reason.trim();
      return setUserAccountStatus(userId, { status, ...(r ? { reason: r } : {}) });
    },
    onSuccess: () => {
      setError(null);
      setConfirmSuspend(false);
      setReason('');
      void qc.invalidateQueries({ queryKey: queryKeys.person(userId) });
    },
    onError: (err: unknown) => {
      setError(
        err instanceof AdminApiError
          ? (ERROR_COPY[err.code] ?? err.message)
          : 'Could not update this account.',
      );
    },
  });

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-6 w-56" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (isError || !data) {
    return <EmptyState title="Person not found" description="This person may have been removed." />;
  }

  const { person, memberships } = data;
  // Fix round 2 finding 1 (Critical): suspension writes identity.users.status
  // globally (modules/identity/api-interface/admin-users-mutations.ts) --
  // tenant_users.status is a per-membership value the suspend action never
  // touches. Deriving the toggle from memberships meant a successful suspend
  // never flipped this button to "Reactivate", and one suspended membership
  // on an otherwise-active account hid the Suspend action entirely.
  const accountStatus = person.accountStatus;
  const isSuspended = accountStatus === 'suspended';
  // locked/deleted are reachable identity.users values this toggle must not
  // misrepresent as a plain active/suspended binary -- an unrecognized status
  // (including the legacy null for a platform.users row with no identity.users
  // mirror yet) hides the toggle rather than guessing which action applies.
  const canToggleSuspend = accountStatus === 'active' || accountStatus === 'suspended';

  return (
    <div className="flex flex-col gap-6 max-w-[900px]">
      <div>
        <Link to="/users" className="text-body-sm text-secondary hover:text-primary">
          People
        </Link>
      </div>

      <div className="flex items-center gap-2.5">
        <h1 className="text-heading-2 text-primary">{person.email}</h1>
        <Badge variant={person.hasActiveMembership ? 'success' : 'neutral'}>
          {person.hasActiveMembership ? 'Active' : 'No active membership'}
        </Badge>
        {accountStatus && (
          <Badge variant={accountStatusVariant(accountStatus)}>Account: {accountStatus}</Badge>
        )}
      </div>

      <section className="rounded-control border border-border bg-surface-elevated p-5">
        <h2 className="text-eyebrow uppercase text-muted mb-3">Memberships</h2>
        <p className="text-caption text-muted mb-3">
          Suspending here disables the account across every tenant. To change one membership, open
          that tenant page.
        </p>
        {memberships.length === 0 ? (
          <p className="text-body-sm text-muted">No tenant memberships.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table dense>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Tenant</TableHeaderCell>
                  <TableHeaderCell>Role</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                  <TableHeaderCell>Accepted</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {memberships.map((m) => (
                  // Fix round 3 finding: tenant_users is unique on (user_id,
                  // tenant_id, role), not (user_id, tenant_id) -- a person
                  // holding two roles in one tenant is two rows with the SAME
                  // tenantId. Keying on tenantId alone collides; React's
                  // reconciliation on a duplicate key is undefined (stale
                  // content or a vanished row on re-render).
                  <TableRow key={`${m.tenantId}:${m.role}`}>
                    <TableCell>{m.tenantName}</TableCell>
                    <TableCell>{m.role}</TableCell>
                    <TableCell>
                      <Badge variant={statusVariant(m.status)}>{m.status}</Badge>
                    </TableCell>
                    <TableCell>
                      {m.acceptedAt ? new Date(m.acceptedAt).toLocaleDateString() : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </section>

      {canManage && (
        <section className="rounded-control border border-border bg-surface-elevated p-5 flex flex-col gap-3">
          <h2 className="text-eyebrow uppercase text-muted">Account actions</h2>

          {error && <p className="text-body-sm text-danger-text">{error}</p>}
          {resetSent && !error && (
            <p className="text-body-sm text-success-text">Password reset email sent.</p>
          )}

          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={async () => {
                try {
                  // Set success state after the await resolves, not inside the
                  // mutation's `onSuccess` callback -- a test double for
                  // `useMutation` (bare `mutateAsync` spy) never invokes
                  // `onSuccess`, so a success assertion here needs the real
                  // component code path, not mock wiring, to be exercised
                  // (fix round 2 finding 8).
                  await resetMutation.mutateAsync();
                  setError(null);
                  setResetSent(true);
                } catch (err) {
                  setError(
                    err instanceof AdminApiError
                      ? (ERROR_COPY[err.code] ?? err.message)
                      : 'Could not send the password reset.',
                  );
                }
              }}
              disabled={resetMutation.isPending}
            >
              Send password reset
            </Button>
            {canToggleSuspend &&
              (!isSuspended ? (
                <Button
                  variant="danger"
                  onClick={() => {
                    setError(null);
                    setConfirmSuspend(true);
                  }}
                >
                  Suspend account
                </Button>
              ) : (
                <Button
                  variant="secondary"
                  onClick={async () => {
                    try {
                      await statusMutation.mutateAsync('active');
                    } catch (err) {
                      setError(
                        err instanceof AdminApiError
                          ? (ERROR_COPY[err.code] ?? err.message)
                          : 'Could not update this account.',
                      );
                    }
                  }}
                  disabled={statusMutation.isPending}
                >
                  Reactivate account
                </Button>
              ))}
          </div>

          {confirmSuspend && (
            <div
              role="alertdialog"
              aria-label="Confirm suspend account"
              className="flex flex-col gap-3 rounded-control border border-danger bg-danger-pale p-4"
            >
              <p className="text-body-sm text-danger-text">
                Suspending {person.email} disables their account across every tenant.
              </p>
              <label className="flex flex-col gap-1 text-body-sm text-secondary">
                Reason (recorded in the audit log)
                <Input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  aria-label="Reason"
                />
              </label>
              <div className="flex gap-2">
                <Button
                  variant="danger"
                  disabled={reason.trim().length === 0 || statusMutation.isPending}
                  onClick={async () => {
                    try {
                      await statusMutation.mutateAsync('suspended');
                    } catch (err) {
                      setError(
                        err instanceof AdminApiError
                          ? (ERROR_COPY[err.code] ?? err.message)
                          : 'Could not update this account.',
                      );
                    }
                  }}
                >
                  Suspend
                </Button>
                <Button variant="ghost" onClick={() => setConfirmSuspend(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
